import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog, IconButton, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Close as CloseIcon } from '@material-ui/icons';

import { useBillingStore } from '../../../../../../store/models';
import { getAddOns } from '../../../../../../conf/chargify';
import CheckoutDrawer from '../../../../../../components/CheckoutDrawer';
import useCheckoutState from '../../../../../../components/CheckoutDrawer/useCheckoutState';
import CategoryToggle from './sections/CategoryToggle';
import PricingSection from '../../../../../../components/Billing/PricingSection';
import mapPlanToCard from '../../../../../../components/Billing/mapPlanToCard';
import { PLAN_CATEGORY, getPlanCategory } from './planCategory';
import { useStyles } from './upgradePlanDialog.styles';

const TITLE_ID = 'upgrade-ecal-plan-dialog-title';

// Width tokens used to cap the dialog to the number of plan cards on a wide
// screen, so one or two cards don't stretch across a full-width dialog. Kept
// in sync with the pricing section grid (card min-width + gap) and the
// paper's horizontal padding. This is only ever used as a max-width ceiling
// now — the pricing grid itself (PricingSection's 'fixed' layout) wraps cards
// onto new rows instead of overflowing once the viewport is narrower than
// this cap, so there's no matching hard minWidth to fight it.
const CARD_WIDTH = 380;
const CARD_GAP = 24;
const PAPER_PADDING_X = 32;
// `body`'s scrollbar gutter (upgradePlanDialog.styles.js) is always reserved,
// even when no scrollbar renders, so the cards grid isn't at the mercy of
// classic-vs-overlay scrollbar rendering (e.g. a mouse connecting on macOS).
// This pads the dialog wide enough to still fit the cards with that gutter
// taken out — deliberately generous rather than exactly derived, since the
// precise padding arithmetic through Paper → body → the cards grid has
// proven easy to get subtly wrong.
const SCROLLBAR_ALLOWANCE = 20;
const MIN_CARDS = 3; // dialog stays wide enough to show three plans side by side, even on the 2-card Community tab
const DEFAULT_MAX_WIDTH = 960; // fallback while plans load (md)

// The plan flagged "Most Popular" in the dialog: ECAL Professional T1.
function isMostPopularPlan (plan) {
    const name = plan.name || '';
    return /professional/i.test(name) && /t1/i.test(name);
}

function UpgradeECALPlanDialog ({ open, onClose, onlyPlanId, onPaymentSuccess, title, currency: currencyOverride, ctaLabel }) {
    const classes = useStyles();
    const theme = useTheme();
    // Dialog-scoped accent — matches the badge/border and CTA button to the app theme's primary color.
    const DIALOG_ACCENT = theme.palette.primary.main;
    const billingStore = useBillingStore();

    const { checkoutOpen, selectedPlan, selectedAddOnValues, handleChoosePlan, closeCheckout } = useCheckoutState();
    const [selectedCategory, setSelectedCategory] = useState(PLAN_CATEGORY.BUSINESS);

    // This dialog subscribes the publisher to a real ECAL plan — unlike the reused
    // Connect CheckoutDrawer's default, which activates a Connect add-on.
    async function subscribeToPlan (token, form) {
        const res = await billingStore.createSubscription({
            code: selectedPlan.code,
            token,
            addOns: getAddOns(selectedAddOnValues, { addOns: selectedPlan.addOns || [] }),
            chargifyInput: {
                firstName: form.firstName,
                lastName: form.lastName,
                address: form.address,
                city: form.city,
                country: form.country,
                state: form.state,
                zip: form.zip,
            },
        });
        if (res.is3DSecure) {
            return { is3DSecure: true };
        }
        if (res.hasCardError) {
            return { error: res.chargifyErr || 'Payment failed. Please check your card details and try again.' };
        }
        return {};
    }

    // Finalizes a 3D-Secure-challenged subscription once the publisher completes
    // authentication — same params shape subscribeToPlan() already builds for
    // billingStore.createSubscription().
    async function finalizeThreeDSecure ({ token, form }) {
        return billingStore.finalize3DSecure({
            code: selectedPlan.code,
            addOns: getAddOns(selectedAddOnValues, { addOns: selectedPlan.addOns || [] }),
            token,
            chargifyInput: {
                firstName: form.firstName,
                lastName: form.lastName,
            },
        });
    }

    // Routes preview through the billing store (real eCAL pricing/tax) instead of
    // CheckoutDrawer's Connect-store default.
    async function previewPlan (input) {
        billingStore.coupon = (input.couponCodes && input.couponCodes[0]) || '';
        const res = await billingStore.getSubscriptionPreview({
            code: input.code,
            addOns: input.addOns,
            chargifyInput: {
                country: input.country,
                state: input.state,
                zip: input.zip,
                city: input.city,
            },
        });
        if (!res || !res.billingPreview) {
            return { error: true };
        }
        return { data: res.billingPreview };
    }

    useEffect(() => {
        billingStore.getPlanDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // planDetails is a uuid change-token (set once getPlanDetails resolves), so
    // coerce it to a "resolved" boolean to gate the loading spinner.
    const plansResolved = !!billingStore.observed.planDetails;

    const currentPlan = billingStore.publisherPlan;
    const currency = currencyOverride || (currentPlan && currentPlan.currency);

    // Use fullPlans (the complete list) rather than planDetails, which the store
    // trims to upgrade-only — we want the current plan shown too. Then keep the
    // current plan and everything priced at or above it (no downgrades).
    const cards = useMemo(() => {
        const source = (billingStore.fullPlans && billingStore.fullPlans.length)
            ? billingStore.fullPlans
            : (billingStore.planDetails || []);
        const mapped = source.map((plan) => mapPlanToCard(plan, currency, isMostPopularPlan(plan)));
        // Signup mode: caller already picked one plan (via the plan dropdown), so
        // show only its details rather than the full upgrade grid.
        if (onlyPlanId) {
            return mapped.filter((card) => card.id === onlyPlanId || card.code === onlyPlanId);
        }
        const currentPrice = currentPlan && currentPlan.price != null
            ? Number(currentPlan.price)
            : null;
        return mapped.filter((card) => {
            if (currentPrice == null || Number.isNaN(currentPrice)) return true;
            const price = Number(card.price.monthly);
            return Number.isNaN(price) || price >= currentPrice;
        });
        // fullPlans/planDetails are plain fields; observed.planDetails is the token that changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingStore.observed.planDetails, currentPlan, currency, onlyPlanId]);

    // Split the upgrade-eligible cards into the two dialog pages. The toggle is
    // shown only when the Community page has at least one plan; otherwise the
    // Business & Enterprise page is shown on its own. In single-plan mode there's
    // nothing to split — the toggle naturally stays hidden (communityCards is empty).
    const businessCards = cards.filter((card) => getPlanCategory(card) === PLAN_CATEGORY.BUSINESS);
    const communityCards = cards.filter((card) => getPlanCategory(card) === PLAN_CATEGORY.COMMUNITY);
    const showToggle = !onlyPlanId && communityCards.length > 0;
    const visibleCards = onlyPlanId
        ? cards
        : (selectedCategory === PLAN_CATEGORY.COMMUNITY ? communityCards : businessCards);

    // Cap the dialog width (on wide screens) to fit the visible cards. Floored at
    // three cards so the Business page's width also covers the two-card Community
    // page — switching tabs doesn't resize the dialog. Single-plan mode has no
    // other page to match, so it caps to just its one card instead. The pricing
    // grid's own wrap-on-narrow behavior (see pricingSection.styles.js) handles
    // shrinking on small screens — there's no matching minWidth to fight that.
    const cardCountForWidth = onlyPlanId ? 1 : Math.max(visibleCards.length, MIN_CARDS);
    const dialogMaxWidth = visibleCards.length > 0
        ? (cardCountForWidth * CARD_WIDTH) + ((cardCountForWidth - 1) * CARD_GAP) + (PAPER_PADDING_X * 2) + SCROLLBAR_ALLOWANCE
        : DEFAULT_MAX_WIDTH;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby={TITLE_ID}
            // width: '100%' (rather than MUI's own `fullWidth` prop, which applies
            // `width: calc(100% - 64px)` as a class) so `maxWidth` is a reliable
            // target size on wide screens instead of sometimes being undercut by
            // that class on viewports narrower than dialogMaxWidth + 64px — which
            // was silently shrinking the dialog below what 3 cards need on plenty
            // of ordinary "big" screens.
            PaperProps={{ className: classes.paper, style: { width: '100%', maxWidth: dialogMaxWidth } }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(10,14,40,0.45)',
                    backdropFilter: 'blur(3px)',
                },
            }}
        >
            <div className={classes.header}>
                <div>
                    <Typography id={TITLE_ID} className={classes.title}>
                        {title}
                    </Typography>
                    {currentPlan && currentPlan.name && (
                        <Typography className={classes.subtitle}>
                            Currently on <strong>{currentPlan.name}</strong>
                        </Typography>
                    )}
                </div>
                {plansResolved && showToggle && (
                    <CategoryToggle
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                    />
                )}
                <IconButton
                    aria-label="Close"
                    className={classes.closeButton}
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>

            <div className={classes.body}>
                <PricingSection
                    plans={visibleCards}
                    loading={!plansResolved}
                    currency={currency}
                    accentColor={DIALOG_ACCENT}
                    currentPlanId={currentPlan && currentPlan.id}
                    onChoosePlan={handleChoosePlan}
                    defaultAddOnsExpanded={!!onlyPlanId}
                    ctaLabel={ctaLabel}
                    cardWidth={CARD_WIDTH}
                    cardGap={CARD_GAP}
                />
            </div>

            <CheckoutDrawer
                open={checkoutOpen}
                plan={selectedPlan}
                addOnValues={selectedAddOnValues}
                onClose={closeCheckout}
                subscribeAction={subscribeToPlan}
                onSubscribed={() => billingStore.getCurrentPlan()}
                previewAction={previewPlan}
                threeDSecureStore={billingStore}
                finalize3DSecureAction={finalizeThreeDSecure}
                productLabel=""
                payLabel="SUBSCRIBE"
                onSuccess={({ planName }) => {
                    closeCheckout();
                    onClose();
                    if (onPaymentSuccess) {
                        // onlyPlanId means this dialog is running in signup mode (single
                        // preselected plan) rather than the general upgrade grid.
                        if (onlyPlanId) {
                            onPaymentSuccess('Success!', `Congratulations! You\'ve signed up for the ${planName}.`);
                        } else {
                            onPaymentSuccess('Plan Upgraded!', `Congratulations! Your plan has been upgraded to ${planName}.`);
                        }
                    }
                }}
            />
        </Dialog>
    );
}

UpgradeECALPlanDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onlyPlanId: PropTypes.string,
    onPaymentSuccess: PropTypes.func,
    title: PropTypes.string,
    currency: PropTypes.string,
    ctaLabel: PropTypes.string,
};

UpgradeECALPlanDialog.defaultProps = {
    title: 'Upgrade Plan',
};

const ObservedUpgradeECALPlanDialog = observer(UpgradeECALPlanDialog);
export default ObservedUpgradeECALPlanDialog;
